import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faPlus, faCheck, faSearch, faThumbtack } from '@fortawesome/free-solid-svg-icons';

const GROUPS = [
  { key: 'for_me', label: 'For me' },
  { key: 'team', label: 'Team' },
  { key: 'tools', label: 'Tools' },
];

const PIN_SEARCH_DEBOUNCE_MS = 300;

/**
 * Catalog drawer. The list is exactly what the server returned for this
 * user, so anything not visible here is not addable by design. The Pinned
 * section searches objects the user may open and pins them directly.
 */
const AddWidgetDrawer = ({ open, catalog, addedTypes, pinnedKeys, onAdd, onPin, onPinSearch, onClose }) => {
  const [query, setQuery] = useState('');
  const [pinQuery, setPinQuery] = useState('');
  const [pinResults, setPinResults] = useState([]);
  const [pinSearching, setPinSearching] = useState(false);
  const [pinError, setPinError] = useState(null);
  const pinTimerRef = useRef(null);
  const pinSeqRef = useRef(0);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (pinTimerRef.current) clearTimeout(pinTimerRef.current);
    const q = pinQuery.trim();
    if (q.length < 2 || !onPinSearch) {
      setPinResults([]);
      setPinSearching(false);
      setPinError(null);
      return undefined;
    }
    setPinSearching(true);
    const seq = ++pinSeqRef.current;
    pinTimerRef.current = setTimeout(async () => {
      try {
        const results = await onPinSearch(q);
        if (seq !== pinSeqRef.current) return;
        setPinResults(Array.isArray(results) ? results : []);
        setPinError(null);
      } catch (err) {
        if (seq !== pinSeqRef.current) return;
        console.error('My Desk: pin search failed', err);
        setPinResults([]);
        setPinError('Search is not available right now.');
      } finally {
        if (seq === pinSeqRef.current) setPinSearching(false);
      }
    }, PIN_SEARCH_DEBOUNCE_MS);
    return () => { if (pinTimerRef.current) clearTimeout(pinTimerRef.current); };
  }, [pinQuery, onPinSearch]);

  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matches = catalog.filter((entry) => (
      entry.addable !== false
      && (!q || entry.title.toLowerCase().includes(q) || (entry.description || '').toLowerCase().includes(q))
    ));
    return GROUPS
      .map((g) => ({ ...g, entries: matches.filter((e) => e.group === g.key) }))
      .filter((g) => g.entries.length > 0);
  }, [catalog, query]);

  if (!open) return null;

  return (
    <div className="mydesk-drawer-root">
      <div className="mydesk-drawer-scrim" onClick={onClose} aria-hidden="true" />
      <aside className="mydesk-drawer" role="dialog" aria-modal="true" aria-label="Add a widget">
        <div className="mydesk-drawer-head">
          <div>
            <h3>Add a widget</h3>
            <p>Only widgets your access allows are listed.</p>
          </div>
          <button type="button" className="mydesk-icon-btn mydesk-icon-btn-lg" onClick={onClose} aria-label="Close">
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <label className="mydesk-drawer-search">
          <FontAwesomeIcon icon={faSearch} />
          <input
            type="search"
            placeholder="Search widgets"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
        </label>

        <div className="mydesk-drawer-list">
          {grouped.length === 0 && (
            <div className="mydesk-drawer-empty">No widgets match your search.</div>
          )}
          {grouped.map((group) => (
            <section key={group.key} className="mydesk-drawer-group">
              <h4>{group.label}</h4>
              {group.entries.map((entry) => {
                const added = addedTypes.has(entry.type);
                return (
                  <div key={entry.type} className="mydesk-drawer-item">
                    <div className="mydesk-drawer-item-text">
                      <div className="mydesk-drawer-item-title">{entry.title}</div>
                      <div className="mydesk-drawer-item-desc">{entry.description}</div>
                    </div>
                    {added ? (
                      <span className="mydesk-chip mydesk-chip-added">
                        <FontAwesomeIcon icon={faCheck} />
                        Added
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="mydesk-chip mydesk-chip-add"
                        aria-label={`Add ${entry.title}`}
                        onClick={() => onAdd(entry.type)}
                      >
                        <FontAwesomeIcon icon={faPlus} />
                        Add
                      </button>
                    )}
                  </div>
                );
              })}
            </section>
          ))}

          <section className="mydesk-drawer-group">
            <h4>Pinned</h4>
            <div className="mydesk-drawer-pin-hint">
              <FontAwesomeIcon icon={faThumbtack} />
              <div>
                <strong>Pin a deal, recap, delivery, invoice, partner or contract</strong>
                <span>Search below, or use <em>Pin to My Workspace</em> on the item&apos;s own screen. Only items you can open are found.</span>
              </div>
            </div>
            <label className="mydesk-drawer-search mydesk-drawer-search-pin">
              <FontAwesomeIcon icon={faSearch} />
              <input
                type="search"
                placeholder="Number, vessel or partner name"
                value={pinQuery}
                onChange={(e) => setPinQuery(e.target.value)}
                aria-label="Find something to pin"
              />
            </label>
            {pinSearching && <div className="mydesk-drawer-empty">Searching</div>}
            {pinError && <div className="mydesk-drawer-empty">{pinError}</div>}
            {!pinSearching && !pinError && pinQuery.trim().length >= 2 && pinResults.length === 0 && (
              <div className="mydesk-drawer-empty">Nothing you can open matches that.</div>
            )}
            {pinResults.map((r) => {
              const key = `${r.entity_type}:${r.object_id}`;
              const already = pinnedKeys.has(key);
              return (
                <div key={key} className="mydesk-drawer-item">
                  <div className="mydesk-drawer-item-text">
                    <div className="mydesk-drawer-item-kicker">{r.kicker}</div>
                    <div className="mydesk-drawer-item-title">{r.title}</div>
                    {r.subtitle && <div className="mydesk-drawer-item-desc">{r.subtitle}</div>}
                  </div>
                  {already ? (
                    <span className="mydesk-chip mydesk-chip-added">
                      <FontAwesomeIcon icon={faCheck} />
                      Pinned
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="mydesk-chip mydesk-chip-add"
                      aria-label={`Pin ${r.title}`}
                      onClick={() => onPin(r.entity_type, r.object_id)}
                    >
                      <FontAwesomeIcon icon={faThumbtack} />
                      Pin
                    </button>
                  )}
                </div>
              );
            })}
          </section>
        </div>
      </aside>
    </div>
  );
};

export default AddWidgetDrawer;
